import React, { ReactNode, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useModal } from '@client/components/common/Modal';
import { MediaDropbox } from '@client/components/common/inputs/ImageDropbox';
import { ErrorVideoModal } from '@client/components/landing-page-builder/Modals';
import { FieldTitle } from '@client/components/landing-page-builder/RedStarTitle';
import Spinner from '@client/components/Spinner';
import { FieldDesc } from '@client/pages/CustomProgramPage/common';
import { maxFileSizeBytes } from '../influencer/videos/videos.constants';
import { UploadConfig } from './helpers/upload';
import { ErrorMessage } from '@client/new/components/Label';
import { isEmpty } from 'lodash';

export const LandingMediaDropbox = ({
  value,
  onChange,
  label,
  type,
  icon,
  children,
  validationText,
  errorMsg,
  inputRef,
  variant = 'gray',
  short,
  optional = false,
  upload,
  width = '240px',
  height = '240px',
}: {
  upload: (f: File, config?: UploadConfig) => Promise<string>;
  value: string | null;
  onChange: (val: string) => void;
  inputRef?: unknown;
  label: string;
  type: 'image' | 'video';
  icon: unknown;
  children?: ReactNode;
  validationText: ReactNode;
  errorMsg?: string;
  variant?: 'gray' | 'white';
  short?: boolean;
  optional?: boolean;
  width?: string;
  height?: string;
}) => {
  const [state, setState] = useState<'initial' | 'uploading' | 'uploaded'>(
    value ? 'uploaded' : 'initial',
  );
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setState(value ? 'uploaded' : 'initial');
  }, [value]);

  const [error, setError] = useState<string | null>(null);
  const [errorType, setVideoErrorType] = useState<
    'duration' | 'format' | 'size' | null
  >(null);

  const videoErrorModal = useModal(ErrorVideoModal);

  function checkFileDuration(file: File): Promise<number> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.src = URL.createObjectURL(file);

      video.onloadedmetadata = function () {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
    });
  }

  const handleFileChange = async (file: File) => {
    if (type === 'video') {
      const videoSizeLimit = maxFileSizeBytes;
      const videoDurationLimit = 90; // 90s
      const format = file.name.split('.').pop()?.toLowerCase();

      const videoDuration = await checkFileDuration(file);

      if (videoDuration > videoDurationLimit) {
        setVideoErrorType('duration');
        videoErrorModal.open();
        return;
      }

      if (format !== 'mp4') {
        setVideoErrorType('format');
        videoErrorModal.open();
        return;
      }

      if (file.size > videoSizeLimit) {
        setVideoErrorType('size');
        videoErrorModal.open();
        return;
      }
    }

    const reader = new FileReader();

    reader.onload = async (event) => {
      if (!event.target?.result) return;

      try {
        setState('uploading');
        setError(null);

        const url = await upload(file, {
          onProgress: (p) => {
            setProgress(p);
          },
        });

        onChange(url);
        setState('uploaded');
      } catch (err) {
        console.error(err);
        setError('Something went wrong!');
        setState('initial');
      }
    };

    reader.readAsDataURL(file);
  };

  const ThumbnailElement = type === 'video' ? 'video' : 'img';
  return (
    <div className="flex flex-col">
      {label ? <FieldTitle optional={optional} title={label} /> : null}
      <FieldDesc className="max-w-[260px]">{validationText}</FieldDesc>
      <div
        className={`${
          short ? 'max-w-[550px] h-[70px]' : ''
        }  flex items-center justify-center flex-grow rounded-[6px]
        ${{ gray: 'bg-[#F1F3F9]', white: 'bg-white' }[variant]}
      `}
        style={{ width, height, maxHeight: height }}
      >
        {state === 'initial' ? (
          <div style={{ width, height }}>
            <MediaDropbox
              hasError={!isEmpty(errorMsg)}
              onChange={handleFileChange}
              icon={icon}
              type={type}
              inputRef={inputRef}
              {...{ short }}
            >
              {children}
            </MediaDropbox>
          </div>
        ) : null}
        {state === 'uploaded' ? (
          <div className="relative h-full w-full flex justify-center">
            <div className="absolute top-[-3px] right-[-3px] flex items-center justify-center bg-white border w-[24px] h-[24px] rounded-full cursor-pointer hover:scale-110">
              <FontAwesomeIcon
                icon={faTimes}
                className="leading-none text-black text-md hover:text-red-500"
                onClick={() => {
                  onChange('');
                  setState('initial');
                }}
              />
            </div>
            <ThumbnailElement
              data-testid="media-dropbox-thumbnail"
              className="h-full rounded-[6px] -z-1 pointer-events-none"
              src={value ? value : ''}
            />
          </div>
        ) : null}
        {state === 'uploading' ? (
          <div>
            <div className="flex flex-col space-y-4 items-center">
              <Spinner />
              {short ? null : (
                <div className="font-semibold text-[#8F9BB3]">
                  Uploading {Math.round(progress)}%
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
      {error ? <div className="text-red-600 mt-2">{error}</div> : null}
      {errorMsg ? (
        <div style={{ marginTop: '10px' }}>
          <ErrorMessage content={errorMsg} />
        </div>
      ) : null}
      {/* {errorMsg ? <div className="text-red-600 mt-2">{errorMsg}</div> : null} */}
      <videoErrorModal.Popup errorType={errorType} />
    </div>
  );
};
